import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCheckbox,
  PrismInput,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import { MAX_INVITE_EMAILS } from 'lumen/adapters/subscription-user';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-multi-select';
import SubscriptionMultiSelect from 'lumen/components/settings/users/subscription-multi-select';
import type { InvitePayload } from 'lumen/services/settings-users';
import { PRODUCTS } from 'lumen/utils/constants/users-constants';

interface ModalInviteUsersSignature {
  Args: {
    onClose: () => void;
    onInvite: (payload: InvitePayload) => Promise<boolean>;
    subscription: SubscriptionOption | null;
  };
}

type ProductOptionId = 'all' | 'bi_benchmark' | 'rate_insight_market_insight';

interface SelectOption extends Option {
  id: string;
}

interface ProductOption extends SelectOption {
  id: ProductOptionId;
}

const PRODUCTS_FOR_OPTION: Record<ProductOptionId, string[]> = {
  all: [PRODUCTS.RATE_INSIGHT, PRODUCTS.BI_BENCHMARKING],
  bi_benchmark: [PRODUCTS.BI_BENCHMARKING],
  rate_insight_market_insight: [PRODUCTS.RATE_INSIGHT],
};

function parseEmails(value: string): string[] {
  if (!value.length) {
    return [];
  }

  const trimmedValues = value
    .split(';')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  return [...new Set(trimmedValues)];
}

export default class ModalInviteUsers extends Component<ModalInviteUsersSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked addToScheduledReports = false;
  @tracked emails = '';
  @tracked isExcludeMode = false;
  @tracked selectedProduct: ProductOption | null =
    this.productOptions[2] ?? null;
  @tracked selectedRole: SelectOption | null = this.roleOptions[2] ?? null;
  @tracked selectedSubscriptions: SubscriptionOption[] = this.args.subscription
    ? [this.args.subscription]
    : [];

  inviteTask = task({ drop: true }, async (): Promise<void> => {
    const product = this.selectedProduct;
    const role = this.selectedRole;

    if (this.hasNoSubscription || !product || !role) {
      return;
    }

    const succeeded = await this.args.onInvite({
      accessRight: role.id,
      copyReports: this.addToScheduledReports,
      emails: this.emailsArray,
      exclude: this.isExcludeMode,
      products: PRODUCTS_FOR_OPTION[product.id],
      subscriptionIds: this.selectedSubscriptions.map((subscription) =>
        Number(subscription.id),
      ),
    });

    if (succeeded) {
      this.usageAnalytics.track('User Access Users Invited', {
        eventTrigger: 'invite-users-modal',
        product: 'user-access',
        numberOfUsers: this.emailsArray.length,
        role: this.selectedRole?.id ?? null,
        subscription: this.isExcludeMode
          ? 'all'
          : this.selectedSubscriptions.map(({ id }) => id).join(','),
        productAccess: this.selectedProduct?.id ?? null,
      });

      this.args.onClose();
    }
  });

  get emailErrorMessage(): string | undefined {
    if (this.emails.length === 0) {
      return undefined;
    }

    if (this.hasInvalidEmail) {
      return this.intl.t('settings.reports.error.invalid_email');
    }

    if (this.hasTooManyEmails) {
      return this.intl.t('settings.users.invite.max_emails_error', {
        count: MAX_INVITE_EMAILS,
      });
    }

    return undefined;
  }

  get emailsArray(): string[] {
    return parseEmails(this.emails);
  }

  get hasEmailError(): boolean {
    return this.emailErrorMessage !== undefined;
  }

  get hasInvalidEmail(): boolean {
    return this.emailsArray.some((email) => !isValidEmailAddress(email));
  }

  // Select all with nothing unticked is an empty exclude selection.
  get hasNoSubscription(): boolean {
    return this.selectedSubscriptions.length === 0 && !this.isExcludeMode;
  }

  get hasTooManyEmails(): boolean {
    return this.emailsArray.length > MAX_INVITE_EMAILS;
  }

  get isSubmitDisabled(): boolean {
    return (
      this.inviteTask.isRunning ||
      this.emailsArray.length === 0 ||
      this.hasInvalidEmail ||
      this.hasTooManyEmails ||
      this.selectedRole === null ||
      this.hasNoSubscription ||
      this.selectedProduct === null
    );
  }

  @cached
  get productOptions(): ProductOption[] {
    return [
      { id: 'all', label: this.intl.t('settings.users.invite.products_all') },
      {
        id: 'bi_benchmark',
        label: this.intl.t('settings.users.column.bi_benchmarking'),
      },
      {
        id: 'rate_insight_market_insight',
        label: this.intl.t('settings.users.column.rate_insight_market_insight'),
      },
    ];
  }

  @cached
  get roleOptions(): SelectOption[] {
    return [
      { id: 'admin', label: this.intl.t('useraccess_rights_admin') },
      { id: 'write', label: this.intl.t('useraccess_rights_write') },
      { id: 'read', label: this.intl.t('useraccess_rights_read') },
    ];
  }

  @action
  submit(): void {
    void this.inviteTask.perform();
  }

  @action
  toggleScheduledReports(): void {
    this.addToScheduledReports = !this.addToScheduledReports;
  }

  @action
  updateEmails(event: Event): void {
    this.emails = (event.target as HTMLInputElement).value;
  }

  @action
  updateProduct(option: ProductOption): void {
    this.selectedProduct = option;
  }

  @action
  updateRole(option: SelectOption): void {
    this.selectedRole = option;
  }

  @action
  updateSubscriptions(
    selectedOptions: SubscriptionOption[],
    isExcludeMode: boolean,
  ): void {
    this.selectedSubscriptions = selectedOptions;
    this.isExcludeMode = isExcludeMode;
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t "settings.users.invite.title"}}
      data-test-id="modal-invite-users"
    >
      <:default>
        <div class="flex flex-col gap-400 p-500">
          <PrismInput
            @errorMessage={{this.emailErrorMessage}}
            @hasErrors={{this.hasEmailError}}
            @helpText={{t "settings.users.invite.email_helper"}}
            @label={{t "settings.users.invite.email_label"}}
            @onInput={{this.updateEmails}}
            @removePadding={{true}}
            @required={{true}}
            @textarea={{true}}
            @value={{this.emails}}
            data-test-id="modal-invite-users-emails"
          />

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.subscription_label"}}
            </span>
            <SubscriptionMultiSelect
              @emptyLabel={{t "settings.users.invite.subscription_label"}}
              @fullWidth={{true}}
              @isExcludeMode={{this.isExcludeMode}}
              @isSecondary={{true}}
              @onChange={{this.updateSubscriptions}}
              @value={{this.selectedSubscriptions}}
              data-test-id="subscription-select"
            />
          </div>

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.role_label"}}
            </span>
            <PrismSelectInput
              @isSecondary={{true}}
              @onChange={{this.updateRole}}
              @options={{this.roleOptions}}
              @placeholder={{t "settings.users.invite.role_label"}}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedRole}}
            />
          </div>

          <div class="flex flex-col gap-100">
            <span class="text-200--bold">
              {{t "settings.users.invite.products_label"}}
            </span>
            <PrismSelectInput
              @isSecondary={{true}}
              @onChange={{this.updateProduct}}
              @options={{this.productOptions}}
              @placeholder={{t "settings.users.invite.products_label"}}
              @unsafe_matchTriggerWidth={{true}}
              @value={{this.selectedProduct}}
            />
          </div>

          <div class="flex flex-col gap-200">
            <span class="text-200--bold">
              {{t "settings.users.invite.scheduled_reports_label"}}
            </span>
            <PrismCheckbox
              @checked={{this.addToScheduledReports}}
              @onChange={{this.toggleScheduledReports}}
              data-test-id="modal-invite-users-scheduled-reports"
            >
              {{t "settings.users.invite.scheduled_reports_checkbox"}}
            </PrismCheckbox>
          </div>
        </div>
      </:default>

      <:footer>
        <div class="flex h-full items-center justify-end gap-400 px-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
          />
          <PrismButton
            @isDisabled={{this.isSubmitDisabled}}
            @isLoading={{this.inviteTask.isRunning}}
            @label={{t "settings.users.invite_users"}}
            @onClick={{this.submit}}
            data-test-id="modal-invite-users-submit"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
